/* eslint-disable react/no-danger */
import { Box, Button, TextField, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import __ from "web/Layout/Translations";

import Dialog from "web/Components/mui/Dialog";

import { IApiRequestMethods, IApiRequestOpts } from "web/api/apiRequestTypes";

import useDupplicateCardForm from "web/hooks/useDuplicateCardForm/useDuplicateCardForm";

import sanitizeObject from "web/utils/data/parser/object/sanitizeObject";
import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import { DUPLICATE_DATA_TYPE, DUPLICATE_REASONS } from "web/constants/benefits";
import restUrls from "web/constants/restUrls";

import {
  DuplicateCardFormActions,
  DuplicateOrderStatus,
  FormView,
  IDuplicateCardData,
  IDuplicateCardFormState,
  IDuplicateFormItem,
  ISubscriptionReceiver,
} from "web/types/Subscription";
import { Nullable } from "web/types/Utils";

import { request } from "web/api";
import { useAppContext } from "web/context/app";
import { setNotificationError } from "web/features/app/appSlice";

import prepareDuplicateFormsData from "./DuplicateSelect/PrepareDuplicateFormsData/prepareDuplicateFormsData";
import DuplicateSelectMUI from "./DuplicateSelect/duplicateSelectMUI";

interface IDuplicateCardForm {
  duplicateCardData: IDuplicateCardData;
  receivers: string;
  receiverId: string;
  currentReceiver: ISubscriptionReceiver;
  updateReceiversDataFunc?: (newReceiverData: ISubscriptionReceiver) => void;
  setOpenDuplicateForm: (id: Nullable<string>) => void;
  setCurrentDuplicateOrderStatus: (status: DuplicateOrderStatus) => void;
  viewType: FormView;
}

const messages = {
  formSendGeneralError: "Wysyłanie formularza nie powiodło się",
};

const DuplicateCardFormMUI: FC<IDuplicateCardForm> = ({
  duplicateCardData,
  receivers,
  receiverId,
  currentReceiver,
  updateReceiversDataFunc,
  setOpenDuplicateForm,
  setCurrentDuplicateOrderStatus,
  viewType,
}) => {
  const dispatch = useDispatch();
  const { formFieldsState, disptachFormFieldsState } = useDupplicateCardForm();
  const [error, setError] = useState("");
  const [personalDataError, setPersonalDataError] = useState(false);
  const [duplicateMessageDialog, setDuplicateMessageDialog] = useState(false);
  const { isMobile } = useAppContext();
  const { address, jo, mp, own, without__deliver } = DUPLICATE_DATA_TYPE;

  const {
    organizationUnits,
    ownList,
    workPlaces,
    duplicateInfo,
    placeDuplicateDeliveredType: deliveredType,
  } = duplicateCardData;

  const prepOwnList = useMemo(() => {
    return ownList.map((own) => {
      return { value: own };
    });
  }, [ownList]);

  const prepWorkPlaces = useMemo(() => {
    return workPlaces.map((place) => {
      return { value: place };
    });
  }, [workPlaces]);

  const duplicatePlace = [
    ...prepOwnList,
    ...organizationUnits,
    ...prepWorkPlaces,
  ];

  const placeDuplicateDeliveredType = deliveredType || without__deliver;

  useEffect(() => {
    if (placeDuplicateDeliveredType === address) {
      disptachFormFieldsState({
        type: DuplicateCardFormActions.CREATE_ADDRESS_DATA,
      });
    }

    if (
      placeDuplicateDeliveredType !== address &&
      placeDuplicateDeliveredType !== without__deliver &&
      formFieldsState &&
      formFieldsState.place
    ) {
      disptachFormFieldsState({
        type: DuplicateCardFormActions.PLACE_REQUIRED,
      });
    }
  }, [placeDuplicateDeliveredType]);

  const selectArray: IDuplicateFormItem[] = [
    {
      inputType: "select",
      label: __("Powód"),
      type: "reason",
      list: DUPLICATE_REASONS,
      placeholder: __("Wybierz powód"),
      index: 2,
      position: 1,
      showWhen: [address, jo, mp, own, without__deliver],
      required: true,
    },
    {
      inputType: "select",
      label: __("Miejsce dostarczenia duplikatu"),
      type: "place",
      list: duplicatePlace,
      placeholder: __("Wybierz"),
      index: 1,
      position: 4,
      showWhen: [jo, mp, own],
      required:
        placeDuplicateDeliveredType !== address &&
        placeDuplicateDeliveredType !== without__deliver,
    },
  ];

  const textInputsHandler = (
    e:
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLInputElement>,
    type: string,
    required: boolean
  ) => {
    const { value } = e.target;
    disptachFormFieldsState({ type, payload: { value, required } });
  };

  const closeDuplicateFormModal = () => {
    // Close all duplicate forms
    setOpenDuplicateForm(null);
  };

  const submitDuplicateData = useCallback(() => {
    const parsedObj = prepareDuplicateFormsData(
      formFieldsState,
      placeDuplicateDeliveredType
    );

    const prepParseObj = sanitizeObject(parsedObj);

    const fetchCardData = async () => {
      try {
        const requestUrl = `${restUrls.duplicateCardOrder.replace(
          ":uuid",
          receiverId || receivers
        )}`;

        const requestOpts: IApiRequestOpts = {
          method: IApiRequestMethods.POST,
          body: JSON.stringify({ data: { ...prepParseObj } }),
        };

        const response = (await request(requestUrl, requestOpts)) as {
          status: string;
        };

        if (response.status === "active") {
          // Prepare changed receiver item for benefitItem state
          const prepReceiverData = {
            ...currentReceiver,
            duplicateOrderStatus: DuplicateOrderStatus.JUST_ORDERED,
          };

          // Update for benefitItem state
          if (typeof updateReceiversDataFunc === "function")
            updateReceiversDataFunc(prepReceiverData);

          // Close all duplicate forms
          setOpenDuplicateForm(null);
          setCurrentDuplicateOrderStatus(DuplicateOrderStatus.JUST_ORDERED);
        }
      } catch (errorData: any) {
        const hasPersonalDataNotChange =
          typeof errorData.message === "string"
            ? errorData.message.includes("PERSONAL_DATA_NOT_CHANGE")
            : false;
        if (hasPersonalDataNotChange && errorData?.response?.status === 422) {
          setDuplicateMessageDialog(true);
        } else setError(__(messages.formSendGeneralError));
        dispatch(
          setNotificationError({
            message: __(messages.formSendGeneralError),
          })
        );
        console.error(errorData);
      }
    };

    fetchCardData();
  }, [formFieldsState, placeDuplicateDeliveredType]);

  const isDisabled = useMemo(() => {
    return Object.keys(formFieldsState)
      .map((key) => [
        key,
        formFieldsState[key as keyof IDuplicateCardFormState],
      ])
      .some((field) => {
        if (isArrayHasItems(field) && field[1]) {
          const fieldElement = field[1];
          const isNotString = typeof fieldElement !== "string";
          return isNotString && !fieldElement.value && fieldElement.required;
        }

        return true;
      });
  }, [formFieldsState]);

  return (
    <>
      <Box sx={{ maxWidth: "512px" }}>
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          {__("Zamówienie duplikatu")}
        </Typography>
        {isArrayHasItems(selectArray) &&
          selectArray
            .sort((a, b) => a.position - b.position)
            .map((formItem) => {
              return (
                <DuplicateSelectMUI
                  key={formItem.position}
                  formItem={formItem}
                  disptachFormFieldsState={disptachFormFieldsState}
                  formFieldsState={formFieldsState}
                  textInputsHandler={textInputsHandler}
                  placeDuplicateDeliveredType={placeDuplicateDeliveredType}
                  personalDataError={personalDataError}
                  setPersonalDataError={setPersonalDataError}
                />
              );
            })}
        <Box>
          <TextField
            sx={{
              width: "100%",
              mb: 2,
            }}
            label={__("Uwagi")}
            multiline
            rows={6}
            slotProps={{ inputLabel: { shrink: true } }}
            inputProps={{ maxLength: 255 }}
            value={formFieldsState.userComment?.value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              textInputsHandler(e, DuplicateCardFormActions.USER_COMMENT, false)
            }
          />
        </Box>
        <Box>
          <Typography variant="body2">*{__("Pola wymagane")}</Typography>
          {error && (
            <Typography variant="body1" color="error" sx={{ my: 1 }}>
              {error}
            </Typography>
          )}
          <Box sx={{ my: 2 }}>
            <Typography
              variant="body1"
              className="cke_editable"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(duplicateInfo as string),
              }}
            />
          </Box>
        </Box>

        <Dialog
          sx={{
            "& .MuiDialog-paper": {
              minWidth: isMobile ? 0 : "612px",
            },
            "& .MuiDialogContent-root": {
              pb: 0,
            },
            "& .MuiDialogTitle-root": {
              pt: 0,
            },
            ".MuiPaper-root": {
              p: 3,
            },
          }}
          // fullScreen={isMobile}
          open={duplicateMessageDialog}
          onClose={() => setDuplicateMessageDialog(false)}
          title={__("Wpisane nowe dane są takie same jak dotychczas.")}
          noTitleDivider
        >
          <Typography variant="body1">
            {__(
              "Nie ma możliwości zamówienia duplikatu z powodu zmiany danych personalnych."
            )}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              pt: 2,
              mt: 2,
              borderTop: "1px solid #ccc",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Button
              sx={{ mr: 1, ...(isMobile && { order: 1, mr: 0, mt: 1 }) }}
              variant="outlined"
              color="primary"
              size="large"
              fullWidth={isMobile}
              onClick={() => {
                setDuplicateMessageDialog(false);
                setOpenDuplicateForm(null);
              }}
            >
              {__("Anuluj zamówienie")}
            </Button>

            <Button
              variant="contained"
              color="primary"
              fullWidth={isMobile}
              size="large"
              onClick={() => {
                setDuplicateMessageDialog(false);
              }}
            >
              {__("Zmień dane / powód")}
            </Button>
          </Box>
        </Dialog>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: {
            xs: "space-between",
            md:
              viewType === FormView.MY_SUBSCRIPTIONS
                ? "flex-end"
                : "space-between",
          },
        }}
      >
        <Button
          sx={{ mr: 2 }}
          variant="outlined"
          color="inherit"
          size="large"
          onClick={() => closeDuplicateFormModal()}
        >
          {__("Anuluj")}
        </Button>
        <Button
          sx={{ minWidth: "197px" }}
          disabled={isDisabled}
          onClick={submitDuplicateData}
          fullWidth={isMobile}
          variant="contained"
          color="primary"
          size="large"
        >
          {__("Wyślij zamówienie")}
        </Button>
      </Box>
    </>
  );
};
export default DuplicateCardFormMUI;
